/**
 * Viewer Page
 * 
 * Displays embedded Looker dashboard using the Embed SDK.
 * Uses cookieless embedding for browser compatibility.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { embedApi } from '../services/api';
import { useSudo } from '../hooks/useSudo';
import type {
  LookerEmbedSDK,
  CookielessSessionResponse,
  TokenRequest,
  SessionStatusEvent,
} from '../types';

export function ViewerPage() {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();
  const { sudoUser } = useSudo();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const embedInitialized = useRef(false);

  // Load Looker Embed SDK from CDN
  useEffect(() => {
    if (window.LookerEmbedSDK || window.getEmbedSDK) {
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@looker/embed-sdk@2.0.6/dist/main.js';
    script.async = true;
    script.onload = () => {
      console.log('[Viewer] Looker Embed SDK loaded');
      setSdkLoaded(true);
    };
    script.onerror = () => {
      console.error('[Viewer] Failed to load Looker Embed SDK');
      setError('Failed to load dashboard viewer. Please refresh the page.');
      setIsLoading(false);
    };

    document.head.appendChild(script);
  }, []);

  // Acquire session callback (passes sudoUser ID for admin impersonation)
  const acquireSession = useCallback(async (): Promise<CookielessSessionResponse> => {
    console.log('[Viewer] Acquiring cookieless session...', sudoUser ? `(sudo: ${sudoUser.id})` : '');
    const data = await embedApi.acquireSession(sudoUser?.id);
    console.log('[Viewer] Session acquired successfully');
    return data;
  }, [sudoUser]);

  // Generate tokens callback (passes sudoUser ID for admin impersonation)
  const generateTokens = useCallback(async (tokens: TokenRequest): Promise<CookielessSessionResponse> => {
    console.log('[Viewer] Refreshing tokens...');
    const data = await embedApi.generateTokens(tokens, sudoUser?.id);
    console.log('[Viewer] Tokens refreshed');
    return data;
  }, [sudoUser]);

  // Initialize dashboard embed
  useEffect(() => {
    if (!sdkLoaded || !dashboardId || embedInitialized.current) {
      return;
    }

    const container = document.getElementById('dashboard-embed-container');
    if (!container) {
      console.log('[Viewer] Container not ready, waiting...');
      return;
    }

    const initEmbed = async () => {
      try {
        // Validate dashboard access before embedding
        const validation = await embedApi.validateDashboard(dashboardId, sudoUser?.id);
        if (!validation.allowed) {
          setError('You do not have access to this dashboard.');
          setIsLoading(false);
          return;
        }

        // Get SDK instance
        let embedSDK: LookerEmbedSDK | null = null;
        if (window.LookerEmbedSDK?.getEmbedSDK) {
          embedSDK = window.LookerEmbedSDK.getEmbedSDK();
        } else if (window.getEmbedSDK) {
          embedSDK = window.getEmbedSDK();
        }

        if (!embedSDK) {
          throw new Error('Looker Embed SDK not available');
        }

        console.log('[Viewer] SDK available, initializing...');
        console.log('[Viewer] Dashboard ID:', dashboardId);

        // Get Looker host from backend
        const lookerHost = await embedApi.getLookerHost();
        console.log('[Viewer] Looker host:', lookerHost);

        embedInitialized.current = true;

        // Initialize SDK with cookieless embedding
        embedSDK.initCookieless(lookerHost, acquireSession, generateTokens);

        console.log('[Viewer] SDK initialized, creating dashboard embed...');

        // Pre-fetch session to get user's theme before building embed
        const initialSession = await embedApi.acquireSession(sudoUser?.id);
        const userThemeName = initialSession.theme_name;

        // Create and embed the dashboard with user's theme (if set)
        const embedParams: Record<string, string> = {};
        
        if (userThemeName) {
          embedParams.theme = userThemeName;
          console.log('[Viewer] Using user theme:', userThemeName);
        }

        const builder = embedSDK
          .createDashboardWithId(dashboardId)
          .withParams(embedParams)
          .appendTo('#dashboard-embed-container')
          .on('dashboard:loaded', () => {
            console.log('[Viewer] Dashboard loaded');
            setIsLoading(false);
          })
          .on('dashboard:run:start', () => {
            console.log('[Viewer] Dashboard query started');
          })
          .on('dashboard:run:complete', () => {
            console.log('[Viewer] Dashboard query complete');
          })
          .on('session:status', (event: unknown) => {
            const status = event as SessionStatusEvent;
            console.log('[Viewer] Session status:', status);
            if (status.expired) {
              setError('Your session has expired. Please refresh the page.');
            }
          });

        console.log('[Viewer] Building embed...');
        const embed = builder.build();

        console.log('[Viewer] Connecting...');
        await embed.connect();

        console.log('[Viewer] Dashboard connected successfully');
        setTimeout(() => setIsLoading(false), 500);
      } catch (err) {
        console.error('[Viewer] Error initializing embed:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        setIsLoading(false);
      }
    };

    initEmbed();
  }, [sdkLoaded, dashboardId, acquireSession, generateTokens, sudoUser]);

  // Handle missing dashboard ID
  useEffect(() => {
    if (!dashboardId) {
      setError('No dashboard specified. Please select a dashboard from the gallery.');
      setIsLoading(false);
    }
  }, [dashboardId]);

  return (
    <Box sx={{ height: '100%', position: 'relative', bgcolor: 'white', overflow: 'hidden' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            zIndex: 999,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading dashboard...
          </Typography>
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            zIndex: 999,
          }}
        >
          <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={() => navigate('/')}>
            Return to Dashboard Gallery
          </Button>
        </Box>
      )}

      {/* Dashboard embed container */}
      <Box
        id="dashboard-embed-container"
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          '& iframe': {
            width: '100%',
            height: '100%',
            border: 'none',
          },
        }}
      />
    </Box>
  );
}

export default ViewerPage;
